<template>
  <WizardModal
    :show-modal="isVisible"
    :modal-title="modalTitle"
    :steps="wizardSteps"
    :step-registry="stepRegistry"
    :wizard-state="wizardState"
    :dynamic-steps="true"
    :orientation="'horizontal'"
    :show-step-description="true"
    :custom-buttons="true"
    :back-label="computedBackLabel"
    :next-label="computedNextLabel"
    :disable-next="computedDisableNext"
    :loading="isSaving"
    :reset-on-close="true"
    @finish="handleFinish"
    @cancel="closeModal"
    @close="closeModal"
    @dismiss="closeModal"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useFranquias } from '../../composables/useFranquias';
import { log } from '../../utils/DebugLogger';
import { useCustomerInternationalConfig } from '../../composables/useCustomerInternationalConfig';

// Import steps
import FranquiasWizardStep1 from './FranquiasWizardStep1.vue';
import FranquiasWizardStep2 from './FranquiasWizardStep2.vue';
import FranquiasWizardStep5 from './FranquiasWizardStep5.vue';
import FranquiasWizardStep6 from './FranquiasWizardStep6.vue';

type Id = number | string;

interface Passo1 {
  nome: string;
  email: string;
  cnpj: string;
  pais_id: Id | null;
  pais_nome: string;
  pais_code: string;
}

interface Passo2 {
  areas_atuacao: unknown;
}

interface Passo3 {
  cnpj: string;
  inscricao_estadual: string;
  endereco: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  cep: string;
}

interface Passo4 {
  mensagem_aniversariante?: string;
  [key: string]: unknown;
}

interface FormData {
  passo1: Passo1;
  passo2: Passo2;
  passo3: Passo3;
  passo4: Passo4;
}

interface FranquiaInner {
  Nome?: string;
  Email?: string;
  CNPJ?: string;
  PaisId?: Id;
  Pais?: string;
  PaisNome?: string;
  [key: string]: unknown;
}

interface FranquiaResponseDados {
  Franquia?: FranquiaInner;
  Pais?: string;
  AreasAtuacao?: unknown;
  [key: string]: unknown;
}

interface FranquiaApiResponse {
  success?: boolean;
  dados?: FranquiaResponseDados;
  error?: string;
}

interface ConfiguracaoApiResponse {
  success?: boolean;
  dados?: Partial<Passo3> & Partial<Passo4>;
}

interface SalvarApiResponse {
  success?: boolean;
  error?: string;
}

interface ModalOpenPayload {
  id?: Id;
  useWizard?: boolean;
}

interface LocationEventPayload {
  franquiaId?: Id;
}

interface WizardState {
  franquiaId: Id | null;
  franquiaData: FranquiaResponseDados | null;
  formData: typeof formData;
}

const { t } = useI18n();
const { fetchFranquia, fetchConfiguracaoFranquia, saveFranquia, saveWizardConfig } = useFranquias();

// Country configuration for internationalization (reusing from cliente module)
const {
  setCountryFromName,
  setDocumentConfig,
  selectedDocumentType,
  currentDocumentPresentation,
  setSelectedDocumentType,
  currentCountry,
  allowedDocumentOptions,
  config: wizardCountryConfig,
} = useCustomerInternationalConfig();

// Emits
const emit = defineEmits<{
  saved: [];
  close: [];
}>();

// Estado local
const isVisible = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const error = ref<string | null>(null);

// Dados da franquia
const franquiaId = ref<Id | null>(null);
const franquiaData = ref<FranquiaResponseDados | null>(null);

// Dados do formulário (reactive)
const formData = ref<FormData>({
  // Passo 1: Informações da Franquia (readonly)
  passo1: {
    nome: '',
    email: '',
    cnpj: '',
    pais_id: null,
    pais_nome: '',
    pais_code: '',
  },

  // Passo 2: Áreas de Atuação
  passo2: {
    areas_atuacao: null,
  },

  // Passo 3: Dados Fiscais (era Passo 5)
  passo3: {
    cnpj: '',
    inscricao_estadual: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    cep: '',
  },

  // Passo 4: Configurações da Unidade (era Passo 6)
  passo4: {
  },
});

// Validação por passo
const stepValidation = ref<Record<number, boolean>>({
  0: false, // Passo 1: Informações da Franquia
  1: false, // Passo 2: Áreas de Atuação
  2: false, // Passo 3: Dados Fiscais
  3: true,  // Passo 4: Configurações Unidade (opcional)
});

// Definição dos steps (WizardModal padrão)
const wizardSteps = [
  { title: t('Informações da Franquia'), key: 'informacoes' },
  { title: t('Áreas de Atuação'), key: 'areas-atuacao' },
  { title: t('Dados Fiscais'), key: 'dados-fiscais' },
  { title: t('Resumo da Franquia'), key: 'configuracoes-unidade' }
];

// Wizard state object (reactivo)
const wizardState = ref<WizardState>({
  franquiaId: null,
  franquiaData: null,
  formData: formData,
});

// Computed - Documento principal (prioriza passo 3 sobre passo 1)
const documentoPrincipal = computed(() => {
  return formData.value.passo3.cnpj || formData.value.passo1.cnpj || '';
});

// Watch - Sincroniza documento do passo 3 com o passo 1
watch(() => formData.value.passo3.cnpj, (novoDocumento) => {
  if (novoDocumento) {
    formData.value.passo1.cnpj = novoDocumento;
  }
}, { immediate: true });

// Step Registry - configuração dinâmica dos steps
const stepRegistry = {
  // Passo 1: Informações da Franquia
  informacoes: {
    component: FranquiasWizardStep1,
    getProps: () => ({
      franquiaId: wizardState.value.franquiaId,
      franquiaData: wizardState.value.franquiaData,
      formData: formData.value.passo1,
      documento: documentoPrincipal.value,
      documentLabel: currentDocumentPresentation.value?.label || t('Documento')
    }),
    getEvents: () => ({
      'validation-change': (isValid: boolean): void => {
        stepValidation.value[0] = isValid;
      },
      'form-update': (data: Partial<Passo1>): void => {
        Object.assign(formData.value.passo1, data);

        // Prefer the ISO code (already resolved from the registry row the
        // user picked) — setCountryFromName tolerates both, but the code is
        // the source of truth once available.
        const countrySignal = data.pais_code || data.pais_nome;
        if (countrySignal) {
          setCountryFromName(countrySignal);
          void loadDocumentConfig(franquiaId.value);
        }
      },
    }),
    validate: () => stepValidation.value[0],
  },

  // Passo 2: Áreas de Atuação
  'areas-atuacao': {
    component: FranquiasWizardStep2,
    getProps: () => ({
      franquiaId: wizardState.value.franquiaId,
      areasAtuacao: formData.value.passo2.areas_atuacao
    }),
    getEvents: () => ({
      'validation-change': (isValid: boolean): void => {
        stepValidation.value[1] = isValid;
      },
      'form-update': (data: Partial<Passo2>): void => {
        Object.assign(formData.value.passo2, data);
      },
      'refresh-areas': async (): Promise<void> => {
        // Recarregar áreas de atuação
        if (franquiaId.value) {
          await loadFranquiaData(franquiaId.value);
        }
      },
    }),
    validate: () => stepValidation.value[1],
  },

  // Passo 3: Dados Fiscais
  'dados-fiscais': {
    component: FranquiasWizardStep5,
    getProps: () => ({
      formData: formData.value.passo3,
      selectedDocumentType: selectedDocumentType.value,
      currentDocumentPresentation: currentDocumentPresentation.value?.label || t('CNPJ'),
      setSelectedDocumentType: setSelectedDocumentType,
      allowedDocumentOptions: allowedDocumentOptions.value,
      countryConfig: wizardCountryConfig.value
    }),
    getEvents: () => ({
      'validation-change': (isValid: boolean): void => {
        stepValidation.value[2] = isValid;
      },
      'form-update': (data: Partial<Passo3>): void => {
        Object.assign(formData.value.passo3, data);
      },
      'cnpj-change': (cnpjValue: string): void => {
        // Sincronizar CNPJ do passo 3 com o passo 1
        formData.value.passo1.cnpj = cnpjValue;
      },
    }),
    validate: () => stepValidation.value[2],
  },

  // Passo 4: Resumo da Franquia (read-only)
  'configuracoes-unidade': {
    component: FranquiasWizardStep6,
    getProps: () => ({
      formData: formData.value,
      countryConfig: wizardCountryConfig.value
    }),
    getEvents: () => ({
      'validation-change': (isValid: boolean): void => {
        stepValidation.value[3] = isValid;
      },
    }),
    validate: () => stepValidation.value[3],
  },
};

// Computed - Título do Modal
const modalTitle = computed<string>(() => {
  if (franquiaData.value && franquiaData.value.Franquia) {
    return t('Configuração da Franquia') + ': ' + (franquiaData.value.Franquia.Nome || '');
  }
  return t('Configuração da Franquia');
});

// Computed - Labels dos botões
// Etapa 1 (index 0): apenas "Próximo" (sem botão de voltar)
// Etapa 4 (index 3): botão "Voltar" e "Concluir"
const computedBackLabel = computed(() => (step: number): string | null => {
  if (step === 0) {
    return null; // Sem botão voltar na primeira etapa
  }
  return t('Voltar');
});

const computedNextLabel = computed(() => (step: number): string => {
  if (isSaving.value) {
    return t('Salvando...');
  }
  if (step === 3) { // Etapa 4 (index 3) - última etapa
    return t('Concluir');
  }
  return t('Próximo');
});

const computedDisableNext = computed(() => {
  return isSaving.value;
});

/**
 * Abre o wizard para editar uma franquia
 */
const openForEdit = async (id: Id): Promise<void> => {
  isVisible.value = true;
  franquiaId.value = id;
  wizardState.value.franquiaId = id;

  // Carregar dados da franquia
  await loadFranquiaData(id);
  await loadConfiguracaoFranquia(id);
};

/**
 * Carrega dados básicos da franquia
 */
const loadFranquiaData = async (id: Id): Promise<void> => {
  try {
    const dados = await fetchFranquia(id as number);

    if (dados) {
      franquiaData.value = dados;
      wizardState.value.franquiaData = dados;

      // Preencher passo 1
      if (dados.Franquia) {
        formData.value.passo1.nome = dados.Franquia.Nome || '';
        formData.value.passo1.email = dados.Franquia.Email || '';
        formData.value.passo1.cnpj = dados.Franquia.CNPJ || '';
        formData.value.passo1.pais_id = dados.Franquia.PaisId || null;

        // Detectar país e configurar internacionalização. `pais_code` is
        // deliberately NOT prefilled here — it is resolved from the
        // locations-service registry once the user (re-)picks a country in
        // Step1 (emitFormUpdate), never guessed from an invented id map (the
        // old paisIdMap here did not match the real registry — Chile is
        // id=11, not 2 — and has been removed rather than replaced).
        const paisNome = dados.Pais || dados.Franquia?.Pais || dados.Franquia?.PaisNome;
        formData.value.passo1.pais_nome = paisNome || '';

        if (paisNome) {
          setCountryFromName(paisNome);
        }
      }

      // Preencher passo 2
      if (dados.AreasAtuacao) {
        formData.value.passo2.areas_atuacao = dados.AreasAtuacao;
      }
    }
  } catch (err) {
    error.value = (err as Error)?.message || '';
  }

  // SEMPRE carregar configuração de documentos
  await loadDocumentConfig(id);
};

/**
 * Configura documentação baseada no país detectado
 */
const loadDocumentConfig = async (_franquiaId?: Id | null): Promise<void> => {
  // Usar o país detectado pelo setCountryFromName (disponível em currentCountry)
  const countryCode = currentCountry.value || 'BR';

  // Configurações por país
  const countryConfigs: Record<string, { allowed_types: string[]; default_type: string }> = {
    'BR': {
      allowed_types: ['cnpj'],
      default_type: 'cnpj'
    },
    'CL': {
      allowed_types: ['rut'],
      default_type: 'rut'
    },
    'AR': {
      allowed_types: ['dni'],
      default_type: 'dni'
    },
    'BO': {
      allowed_types: ['ci'],
      default_type: 'ci'
    },
    'US': {
      allowed_types: ['ssn'],
      default_type: 'ssn'
    },
    'PT': {
      allowed_types: ['nif'],
      default_type: 'nif'
    },
    'MX': {
      allowed_types: ['curp'],
      default_type: 'curp'
    },
    'GT': {
      allowed_types: ['dpi'],
      default_type: 'dpi'
    },
    'PY': {
      allowed_types: ['cedula'],
      default_type: 'cedula'
    },
    'EC': {
      allowed_types: ['cedula_ec', 'ruc'],
      default_type: 'cedula_ec'
    }
  };

  // Obter configuração baseada no país detectado
  const config = countryConfigs[countryCode] || countryConfigs['BR'];

  // Seta a configuração de documentos
  setDocumentConfig({
    document_types: config.allowed_types,
    default_document_type: config.default_type,
  });
};

/**
 * Carrega configuração da franquia
 */
const loadConfiguracaoFranquia = async (id: Id): Promise<void> => {
  try {
    const config = await fetchConfiguracaoFranquia(id as number);

    if (config) {

      // Preencher passo 3 (Dados Fiscais) - o watch sincronizará com o passo 1 automaticamente
      if (config.cnpj) {
        formData.value.passo3.cnpj = config.cnpj;
      } else if (formData.value.passo1.cnpj) {
        formData.value.passo3.cnpj = formData.value.passo1.cnpj;
      }
      if (config.inscricao_estadual) formData.value.passo3.inscricao_estadual = config.inscricao_estadual;
      if (config.endereco) formData.value.passo3.endereco = config.endereco;
      if (config.numero) formData.value.passo3.numero = config.numero;
      if (config.complemento) formData.value.passo3.complemento = config.complemento;
      if (config.bairro) formData.value.passo3.bairro = config.bairro;
      if (config.cidade) formData.value.passo3.cidade = config.cidade;
      if (config.estado) formData.value.passo3.estado = config.estado;
      if (config.cep) formData.value.passo3.cep = config.cep;

      // Preencher passo 4
      if (config.mensagem_aniversariante) formData.value.passo4.mensagem_aniversariante = config.mensagem_aniversariante;
    }
  } catch {
    // Se não houver configuração ainda, não é erro
  }
};

/**
 * Fecha o wizard
 */
const closeModal = (): void => {
  isVisible.value = false;

  // Reset dados
  franquiaId.value = null;
  franquiaData.value = null;
  wizardState.value.franquiaId = null;
  wizardState.value.franquiaData = null;

  // Reset formData
  (Object.keys(formData.value) as Array<keyof FormData>).forEach((key) => {
    const sub = formData.value[key];
    if (typeof sub === 'object' && sub) {
      Object.keys(sub).forEach((k) => {
        (sub as Record<string, unknown>)[k] = null;
      });
    }
  });

  // Reset validations
  Object.keys(stepValidation.value).forEach((key) => {
    stepValidation.value[Number(key)] = Number(key) === 3;
  });

  error.value = null;
  emit('close');
};

/**
 * Finaliza e salva os dados
 */
const handleFinish = async (): Promise<void> => {
  // Validar último passo (confirmação)
  if (!stepValidation.value[3]) {
    return;
  }

  isSaving.value = true;
  error.value = null;

  try {
    // Preparar dados para envio
    const dataToSave: Record<string, unknown> = {
      // Passo 3: Dados Fiscais
      cnpj: formData.value.passo3.cnpj,
      inscricao_estadual: formData.value.passo3.inscricao_estadual,
      endereco: formData.value.passo3.endereco,
      numero: formData.value.passo3.numero,
      complemento: formData.value.passo3.complemento,
      bairro: formData.value.passo3.bairro,
      cidade: formData.value.passo3.cidade,
      estado: formData.value.passo3.estado,
      cep: formData.value.passo3.cep,

      // Passo 4: Configurações da Unidade
      mensagem_aniversariante: formData.value.passo4.mensagem_aniversariante,
    };

    // Remover campos undefined/null
    Object.keys(dataToSave).forEach((key) => {
      if (dataToSave[key] === null || dataToSave[key] === undefined) {
        delete dataToSave[key];
      }
    });

    const franquiaPayload: Record<string, unknown> = {
      ID: franquiaId.value,
      Nome: formData.value.passo1.nome,
      Email: formData.value.passo1.email,
      CNPJ: formData.value.passo1.cnpj,
      // Write the ISO code (S1b: storage must move to ISO) — pais_nome is a
      // fallback only for the case the user never touched the país select
      // (pais_code stays '' until Step1 resolves one from the registry).
      Pais: formData.value.passo1.pais_code || formData.value.passo1.pais_nome,
    };

    Object.keys(franquiaPayload).forEach((key) => {
      if (franquiaPayload[key] === null || franquiaPayload[key] === undefined || franquiaPayload[key] === '') {
        delete franquiaPayload[key];
      }
    });

    log('FranquiasWizard', 'handleFinish', 'Saving main franchise data', franquiaPayload);
    await saveFranquia(franquiaPayload as any);

    await saveWizardConfig(franquiaId.value as number, dataToSave as any);

    isSaving.value = false;
    eventBus.emit('franquias.refresh');
    emit('saved');
    closeModal();
  } catch (err) {
    error.value = (err as Error)?.message || '';
    isSaving.value = false;
  }
};

// Event listeners para abrir o modal via eventBus
// Nota: O Wizard usa um evento diferente do modal padrão para evitar conflitos
// Use 'modal.franquiasWizard.open' para abrir o wizard de configuração completa
const handleModalOpen = (data?: ModalOpenPayload): void => {
  if (data?.id) {
    void openForEdit(data.id);
  }
};

// Handler legado para compatibilidade - 'modal.franquia.open' com flag wizard
const handleModalOpenWithFlag = (data?: ModalOpenPayload): void => {
  // Só abre o wizard se explicitamente solicitado via flag 'useWizard'
  if (data?.useWizard && data?.id) {
    void openForEdit(data.id);
  }
};

// Handler para quando uma localidade é adicionada
const handleLocationAdded = (data?: LocationEventPayload): void => {
  if (data?.franquiaId && franquiaId.value === data.franquiaId) {
    void loadFranquiaData(data.franquiaId);
  }
};

// Handler para quando uma localidade é removida
const handleLocationRemoved = (data?: LocationEventPayload): void => {
  if (data?.franquiaId && franquiaId.value === data.franquiaId) {
    void loadFranquiaData(data.franquiaId);
  }
};

onMounted(() => {
  // Evento principal do wizard - usado para abrir o wizard de configuração completa
  eventBus.on('modal.franquiasWizard.open', handleModalOpen);

  // Evento legado para compatibilidade - só responde se tiver flag 'useWizard'
  // Isso permite que o mesmo evento 'modal.franquia.open' seja usado seletivamente
  eventBus.on('modal.franquia.open', handleModalOpenWithFlag);

  eventBus.on('modal.franquiasWizard.close', closeModal);
  eventBus.on('location-added', handleLocationAdded);
  eventBus.on('location-removed', handleLocationRemoved);
});

onBeforeUnmount(() => {
  eventBus.off('modal.franquiasWizard.open', handleModalOpen);
  eventBus.off('modal.franquia.open', handleModalOpenWithFlag);
  eventBus.off('modal.franquiasWizard.close', closeModal);
  eventBus.off('location-added', handleLocationAdded);
  eventBus.off('location-removed', handleLocationRemoved);
});

// Expor métodos para componentes pais
defineExpose({
  openForEdit,
  closeModal,
});
</script>

<style scoped>
/* WizardModal padrão do sistema já tem todo o styling necessário */
</style>
